import { AnyAction } from 'redux';
import { App, ThhHundVorbelegungdaten } from '@ps20-frontend/frontend-models';
import {
  CheckoutProdukt,
  GlobalActions,
  ProduktDeleted,
  ThhHundProduktPayload,
  TiergruppeAnimal,
} from '@ps20-frontend/shop-integration-redux';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE } from '@ps20-frontend/utils';
import { ThhTier } from '@ps20/bff-models';
import { CheckoutActions } from '../checkout.actions';
import { ThhHundAngabeChangedPayload } from '../action-types/thhHundAngabeChangedPayload';
import { findEntryOfCard, tiergruppeEntryFieldsOf } from '../../utils/tiergruppe-animal.utils';

export interface ThhHundAngaben {
  angabenListe: Array<ThhHundAngabenElement>;
  thhHundAngebotVorhanden: boolean;
  tiere: ThhTier[];
}

export enum ThhHundGeschlecht {
  M = 'M',
  W = 'W',
}

export enum HundKennzeichnung {
  CHIP = 'Chip',
  TAETOWIERUNG = 'Tätowierung',
  KEINE = 'Keine',
}

export interface ThhHundAngabenElement {
  geschlecht: ThhHundGeschlecht;
  kennzeichnung: HundKennzeichnung;
  name: string;
  geburtsdatum: string;
  chipnummer: string;
  taetowierungsnummer: string;
  hunderasse: string;
  hunderasseValue: string;
  vorbelegt?: boolean;
  cardId?: string;
}

export function determineGeschlecht(
  isVorbelegt: boolean,
  tierGeschlecht: string,
  angabenGeschlecht: ThhHundGeschlecht,
): ThhHundGeschlecht {
  if (isVorbelegt) {
    return angabenGeschlecht;
  }

  if (['M', 'B'].includes(tierGeschlecht)) {
    return ThhHundGeschlecht.M;
  }

  if (['W', 'A'].includes(tierGeschlecht)) {
    return ThhHundGeschlecht.W;
  }

  return null;
}

export function determineKennzeichnung(
  isVorbelegt: boolean,
  identifizierungsart: string,
  kennzeichnung: HundKennzeichnung,
): HundKennzeichnung {
  if (isVorbelegt) {
    return kennzeichnung;
  }

  if (identifizierungsart === 'C') {
    return HundKennzeichnung.CHIP;
  } else if (identifizierungsart === 'T') {
    return HundKennzeichnung.TAETOWIERUNG;
  } else {
    return HundKennzeichnung.KEINE;
  }
}

export function determineChipnummer(
  isVorbelegt: boolean,
  identifizierungscode: string,
  identifizierungsart: string,
  chipnummer: string,
): string {
  if (isVorbelegt) {
    return chipnummer;
  }

  if (identifizierungscode && identifizierungsart === 'C') {
    return identifizierungscode;
  } else {
    return null;
  }
}

export function determineTaetowierungsnummer(
  isVorbelegt: boolean,
  identifizierungscode: string,
  identifizierungsart: string,
  taetowierungsnummer: string,
): string {
  if (isVorbelegt) {
    return taetowierungsnummer;
  }

  if (identifizierungscode && identifizierungsart === 'T') {
    return identifizierungscode;
  } else {
    return null;
  }
}

export function determineGeburtsdatum(
  isVorbelegt: boolean,
  geburtsdatumTier: string,
  geburtsdatumAngabenListe: string,
): string {
  if (!isVorbelegt) {
    if (geburtsdatumTier) {
      return dayjs(geburtsdatumTier).format(DATEFORMAT_LOCAL_STATE);
    } else {
      return null;
    }
  } else {
    return geburtsdatumAngabenListe;
  }
}

const tiergruppeEntryOf = (
  entries: ThhHundAngabenElement[],
  animal: TiergruppeAnimal | undefined,
): Partial<ThhHundAngabenElement> => {
  if (!animal) {
    return {};
  }
  return {
    ...findEntryOfCard(entries, animal.cardId),
    ...tiergruppeEntryFieldsOf(animal, gender => determineGeschlecht(false, gender, null)),
  };
};

const entryFor = (
  entries: ThhHundAngabenElement[],
  produkt: ThhHundProduktPayload,
  index: number,
): Partial<ThhHundAngabenElement> => {
  const tiergruppeAnimals = produkt.beibehalten ? undefined : produkt.tiergruppeAnimals;
  return tiergruppeAnimals ? tiergruppeEntryOf(entries, tiergruppeAnimals[index]) : entries[index];
};

export const thhHundAngabenReducer = function (
  sectionState: ThhHundAngaben = null,
  action: AnyAction,
): ThhHundAngaben {
  switch (action.type) {
    case GlobalActions.PRODUKT_ACTION: {
      const produkt: CheckoutProdukt = action.payload;
      if (produkt.appId !== App.THH_HUND) {
        return sectionState;
      }

      const thhHundProdukt: ThhHundProduktPayload = action.payload;
      const emptyHundAngabe: ThhHundAngabenElement = {
        name: null,
        geschlecht: null,
        kennzeichnung: null,
        chipnummer: null,
        geburtsdatum: null,
        taetowierungsnummer: null,
        hunderasse: null,
        hunderasseValue: null,
      };

      let newAngabenListe: Array<ThhHundAngabenElement> = [];

      if (
        !thhHundProdukt.vorvertragsnummer ||
        thhHundProdukt.isWeitererVertrag ||
        thhHundProdukt.tiere?.length < 1 ||
        thhHundProdukt.beibehalten
      ) {
        newAngabenListe = thhHundProdukt.ausgewaehlteHunderassen.map(
          (ausgewaehlteHunderasse, index) => ({
            ...emptyHundAngabe,
            ...entryFor(sectionState.angabenListe, thhHundProdukt, index),
            hunderasseValue: thhHundProdukt.ausgewaehlteHunderassenValue[index],
            hunderasse: ausgewaehlteHunderasse,
          }),
        );
      } else {
        newAngabenListe = thhHundProdukt.tiere?.map((tier, index) => {
          const angabenListe = sectionState.angabenListe[index];
          const isVorbelegt = angabenListe?.vorbelegt;
          return {
            ...emptyHundAngabe,
            ...angabenListe,
            hunderasseValue: thhHundProdukt.ausgewaehlteHunderassenValue[index],
            hunderasse: thhHundProdukt.ausgewaehlteHunderassen[index],
            name: !isVorbelegt ? tier.tiername : angabenListe?.name,
            geschlecht: determineGeschlecht(isVorbelegt, tier.geschlecht, angabenListe?.geschlecht),
            kennzeichnung: determineKennzeichnung(
              isVorbelegt,
              tier.identifizierungsart,
              angabenListe?.kennzeichnung,
            ),
            chipnummer: determineChipnummer(
              isVorbelegt,
              tier.identifizierungscode,
              tier.identifizierungsart,
              angabenListe?.chipnummer,
            ),
            taetowierungsnummer: determineTaetowierungsnummer(
              isVorbelegt,
              tier.identifizierungscode,
              tier.identifizierungsart,
              angabenListe?.taetowierungsnummer,
            ),
            geburtsdatum: determineGeburtsdatum(
              isVorbelegt,
              tier.geburtsdatumTier,
              angabenListe?.geburtsdatum,
            ),
            vorbelegt: true,
          };
        });
      }

      return {
        ...sectionState,
        angabenListe: newAngabenListe,
        thhHundAngebotVorhanden: !produkt.beibehalten,
      };
    }
    case GlobalActions.THH_HUND_VORBELEGUNG_FINISHED: {
      const actionPayload: ThhHundVorbelegungdaten = action.payload.vorhandeneVorbelegungdaten;
      return {
        ...sectionState,
        tiere: actionPayload?.tiere,
      };
    }
    case GlobalActions.PRODUKT_DELETED: {
      const produktDeletedPayload: ProduktDeleted = action.payload;
      if (produktDeletedPayload.angebotsIds.some(angebotsId => angebotsId.appId === App.THH_HUND)) {
        return {
          ...sectionState,
          angabenListe: [],
          thhHundAngebotVorhanden: false,
        };
      }
      return sectionState;
    }
    case CheckoutActions.THH_HUND_ANGABE_CHANGED: {
      const actionPayload: ThhHundAngabeChangedPayload = action.payload;
      const newAngabenListe = sectionState.angabenListe.map((thhHundAngabe, index) => {
        if (index === actionPayload.index) {
          return {
            ...thhHundAngabe,
            ...actionPayload.changedValues,
          };
        } else {
          return thhHundAngabe;
        }
      });
      return {
        ...sectionState,
        angabenListe: newAngabenListe,
      };
    }

    case CheckoutActions.THH_HUND_KENNZEICHENART_CHANGED: {
      const actionPayload: AnyAction = action.payload;
      const newAngabenListe = sectionState.angabenListe.map((thhHundAngabe, index) => {
        if (index === actionPayload.index) {
          return {
            ...thhHundAngabe,
            kennzeichnung: actionPayload.kennzeichnung,
            taetowierungsnummer: sectionState.angabenListe[index].taetowierungsnummer
              ? sectionState.angabenListe[index].taetowierungsnummer
              : null,
            chipnummer: sectionState.angabenListe[index].chipnummer
              ? sectionState.angabenListe[index].chipnummer
              : null,
          };
        } else {
          return thhHundAngabe;
        }
      });
      return {
        ...sectionState,
        angabenListe: newAngabenListe,
      };
    }

    default:
      return sectionState;
  }
};
